import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { createContact } from '../../Action/api';
import { addContactToStore } from '../../Redux/contactsSlice';
import {
  ContactExtraSections,
  getExtraInitialValues,
  buildExtraPayload,
  validateExtraFields,
  COUNTRY_OPTIONS,
  INDUSTRY_OPTIONS,
  COMPANY_SIZE_OPTIONS,
} from '../../Components/ContactForm';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Stack,
  IconButton,
  Divider,
  CircularProgress,
  InputAdornment,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Business,
  Work,
  Language,
  LinkedIn,
  LocationOn,
  Notes,
  ArrowBack,
  CheckCircle,
  Public,
  AccountCircle,
  Category,
  People,
  WhatsApp,
} from '@mui/icons-material';

const createEmptyForm = () => ({
  // Person fields
  fullName: '',
  email: '',
  phoneNumber: '',
  whatsappNumber: '',
  jobTitle: '',
  linkedinProfile: '',
  gender: '',
  // Company fields
  companyName: '',
  companyEmail: '',
  companyPhone: '',
  companyWhatsapp: '',
  industry: '',
  companySize: '',
  // Shared fields
  website: '',
  country: 'India',
  state: '',
  district: '',
  city: '',
  postcode: '',
  address: '',
  notes: '',
  tags: '',
  // Extended profile, education and relationship fields
  ...getExtraInitialValues(),
});

export const CreateContact = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [contactType, setContactType] = useState('person'); // 'person' or 'company'

  const [formData, setFormData] = useState(createEmptyForm);

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [contactId, setContactId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  // Template data for quick fill
  const contactTemplates = {
    person: {
      'Tech Professional': {
        fullName: 'Rajesh Kumar',
        email: 'rajesh.kumar@techcorp.in',
        phoneNumber: '+91 98765 43210',
        whatsappNumber: '+91 98765 43210',
        jobTitle: 'Senior Software Engineer',
        linkedinProfile: 'https://linkedin.com/in/rajeshkumar',
        website: '',
        country: 'India',
        state: 'Karnataka',
        district: 'Bangalore Urban',
        city: 'Bangalore',
        postcode: '560001',
        address: '123 MG Road, Indiranagar',
        notes: 'Experienced in full-stack development',
        tags: 'Technology, Software, Developer',
      },
      'Business Owner': {
        fullName: 'Priya Sharma',
        email: 'priya.sharma@gmail.com',
        phoneNumber: '+91 99887 76655',
        whatsappNumber: '+91 99887 76655',
        jobTitle: 'Entrepreneur',
        linkedinProfile: 'https://linkedin.com/in/priyasharma',
        website: 'https://www.priyadesigns.in',
        country: 'India',
        state: 'Maharashtra',
        district: 'Mumbai',
        city: 'Mumbai',
        postcode: '400001',
        address: '456 Marine Drive, Nariman Point',
        notes: 'Runs a successful design agency',
        tags: 'Business Owner, Design, Creative',
      },
      'Sales Executive': {
        fullName: 'Amit Patel',
        email: 'amit.patel@company.co.in',
        phoneNumber: '+91 97654 32109',
        whatsappNumber: '+91 97654 32109',
        jobTitle: 'Sales Manager',
        linkedinProfile: 'https://linkedin.com/in/amitpatel',
        website: '',
        country: 'India',
        state: 'Gujarat',
        district: 'Ahmedabad',
        city: 'Ahmedabad',
        postcode: '380001',
        address: '789 SG Highway, Satellite',
        notes: 'Specializes in enterprise sales',
        tags: 'Sales, B2B, Enterprise',
      },
    },
    company: {
      'Tech Startup': {
        companyName: 'InnovateTech Solutions Pvt Ltd',
        companyEmail: 'contact@innovatetech.in',
        companyPhone: '+91 80 4567 8900',
        companyWhatsapp: '+91 98765 00000',
        industry: 'Technology',
        companySize: '11-50 employees',
        website: 'https://www.innovatetech.in',
        country: 'India',
        state: 'Karnataka',
        district: 'Bangalore Urban',
        city: 'Bangalore',
        postcode: '560100',
        address: 'WeWork, Prestige Tech Park, Marathahalli',
        notes: 'Leading AI/ML solutions provider',
        tags: 'SaaS, AI, Enterprise, Startup',
      },
      'Manufacturing Firm': {
        companyName: 'Gujarat Industries Ltd',
        companyEmail: 'info@gujaratind.com',
        companyPhone: '+91 79 2345 6789',
        companyWhatsapp: '+91 99999 88888',
        industry: 'Manufacturing',
        companySize: '201-500 employees',
        website: 'https://www.gujaratind.com',
        country: 'India',
        state: 'Gujarat',
        district: 'Ahmedabad',
        city: 'Ahmedabad',
        postcode: '382405',
        address: 'Plot No 45, GIDC Estate, Vatva',
        notes: 'ISO certified manufacturing facility',
        tags: 'Manufacturing, B2B, Export',
      },
      'Consulting Agency': {
        companyName: 'Strategic Consultants & Associates',
        companyEmail: 'hello@strategicconsult.in',
        companyPhone: '+91 22 6789 0123',
        companyWhatsapp: '+91 98888 77777',
        industry: 'Consulting',
        companySize: '51-200 employees',
        website: 'https://www.strategicconsult.in',
        country: 'India',
        state: 'Maharashtra',
        district: 'Mumbai',
        city: 'Mumbai',
        postcode: '400051',
        address: 'Tower A, BKC, Bandra East',
        notes: 'Management consulting for Fortune 500 companies',
        tags: 'Consulting, Strategy, Management',
      },
    },
  };

  const applyTemplate = (templateName) => {
    const template = contactTemplates[contactType][templateName];
    if (template) {
      setFormData({ ...formData, ...template });
      setSelectedTemplate(templateName);
      setErrors({});
    }
  };

  const clearTemplate = () => {
    setSelectedTemplate('');
    setFormData(createEmptyForm());
  };

  const industryOptions = INDUSTRY_OPTIONS;

  const companySizeOptions = COMPANY_SIZE_OPTIONS;

  const countryOptions = COUNTRY_OPTIONS;

  const handleContactTypeChange = (event, newType) => {
    if (newType !== null) {
      setContactType(newType);
      // Clear errors and template when switching types
      setErrors({});
      setSelectedTemplate('');
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  // Setter for fields that hand back a value rather than an event (extra sections, education)
  const handleFieldValue = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key === field || key.startsWith(`${field}.`)) delete next[key];
      });
      return next;
    });
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const re = /^[\d\s\-+()]+$/;
    return re.test(phone) && phone.replace(/\D/g, '').length >= 10;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (contactType === 'person') {
      // Only full name, country, and gender are required
      if (!formData.fullName.trim()) {
        newErrors.fullName = 'Full name is required';
      }
      if (!formData.country) {
        newErrors.country = 'Country is required';
      }
      if (!formData.gender) {
        newErrors.gender = 'Gender is required';
      }
      // Optional email validation - only if provided
      if (formData.email.trim() && !validateEmail(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      // Optional phone validation - only if provided
      if (formData.phoneNumber.trim() && !validatePhone(formData.phoneNumber)) {
        newErrors.phoneNumber = 'Please enter a valid phone number';
      }
      // Optional WhatsApp validation - only if provided
      if (formData.whatsappNumber.trim() && !validatePhone(formData.whatsappNumber)) {
        newErrors.whatsappNumber = 'Please enter a valid WhatsApp number';
      }
    } else {
      // Only company name and country are required
      if (!formData.companyName.trim()) {
        newErrors.companyName = 'Company name is required';
      }
      
      if (!formData.country) {
        newErrors.country = 'Country is required';
      }
      
      // Optional email validation - only if provided
      if (formData.companyEmail.trim() && !validateEmail(formData.companyEmail)) {
        newErrors.companyEmail = 'Please enter a valid email address';
      }
      
      // Optional phone validation - only if provided
      if (formData.companyPhone.trim() && !validatePhone(formData.companyPhone)) {
        newErrors.companyPhone = 'Please enter a valid phone number';
      }
      
      // Optional WhatsApp validation - only if provided
      if (formData.companyWhatsapp.trim() && !validatePhone(formData.companyWhatsapp)) {
        newErrors.companyWhatsapp = 'Please enter a valid WhatsApp number';
      }
    }
    
    Object.assign(newErrors, validateExtraFields(formData, contactType));

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) {
      setErrors((prev) => ({ ...prev, submit: 'Please fix the highlighted fields above.' }));
      // Long form: bring the first invalid field into view
      setTimeout(() => {
        document.querySelector('.Mui-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
    }
    if (isValid) {
      setIsSubmitting(true);
      
      // Prepare contact data based on contact type
      const contactPayload = {
        contactType: contactType,
        ...(contactType === 'person' ? {
          fullName: formData.fullName,
          email: formData.email || null,
          phoneNumber: formData.phoneNumber || null,
          whatsappNumber: formData.whatsappNumber || null,
          jobTitle: formData.jobTitle || null,
          linkedinProfile: formData.linkedinProfile || null,
          gender: formData.gender || null,
        } : {
          companyName: formData.companyName,
          companyEmail: formData.companyEmail || null,
          companyPhone: formData.companyPhone || null,
          companyWhatsapp: formData.companyWhatsapp || null,
          industry: formData.industry || null,
          companySize: formData.companySize || null,
        }),
        website: formData.website || null,
        country: formData.country,
        state: formData.state || null,
        district: formData.district || null,
        city: formData.city || null,
        postcode: formData.postcode || null,
        address: formData.address || null,
        notes: formData.notes || null,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(Boolean) : [],
        ...buildExtraPayload(formData, contactType),
        createdAt: new Date().toISOString(),
      };
      
      try {
        // Call API to create contact
        const response = await createContact(formData.country, contactPayload);
        
        if (response.success) {
          console.log('Contact created successfully:', response.data);
          
          // Get contact ID and dateTimeStamp from API response
          const contactId = response.data?.contactId || `CNT-${Date.now().toString().slice(-8)}`;
          const dateTimeStamp = response.data?.dateTimeStamp || new Date().toISOString();
          
          setContactId(contactId);
          
          // Add the new contact to Redux store with all data from response
          const newContactForStore = {
            ...response.data,
            dateTimeStamp,
            contactId,
          };
          
          dispatch(addContactToStore(newContactForStore));
          console.log('Redux: New contact added to store with ID:', contactId);
          
          setSubmitted(true);
        } else {
          console.error('Failed to create contact:', response.message);
          setErrors({ submit: response.message || 'Failed to create contact. Please try again.' });
        }
      } catch (error) {
        console.error('Error creating contact:', error);
        setErrors({ submit: 'Network error. Please check your connection and try again.' });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleNewContact = () => {
    setSubmitted(false);
    setContactId('');
    setContactType('person');
    setSelectedTemplate('');
    setFormData(createEmptyForm());
    setErrors({});
  };

  // Success view after submission
  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: { xs: 3, sm: 4, md: 8 }, px: { xs: 1, sm: 2, md: 0 } }}>
        <Container maxWidth="md">
          <Paper sx={{ p: { xs: 3, sm: 4, md: 6 }, borderRadius: { xs: 2, md: 3 }, textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <Box
              sx={{
                width: { xs: 60, sm: 70, md: 90 },
                height: { xs: 60, sm: 70, md: 90 },
                borderRadius: '50%',
                bgcolor: '#10b98115',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: { xs: 2, sm: 2.5, md: 3 },
              }}
            >
              <CheckCircle sx={{ fontSize: { xs: 32, sm: 40, md: 50 }, color: '#10b981' }} />
            </Box>
            
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 2, fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' } }}>
              {contactType === 'person' ? 'Contact' : 'Company'} Created Successfully!
            </Typography>
            
            <Typography variant="body1" sx={{ color: '#64748b', mb: { xs: 3, sm: 4 }, fontSize: { xs: '0.875rem', sm: '1rem' }, px: { xs: 1, sm: 0 } }}>
              The {contactType === 'person' ? 'contact' : 'company'} has been added to your database and is ready for follow-up.
            </Typography>
            
            <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, bgcolor: '#f8fafc', mb: { xs: 3, sm: 4 }, border: '1px solid #e2e8f0' }}>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Contact ID
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#3b82f6', mb: 2, fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}>
                {contactId}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2} sx={{ textAlign: 'left' }}>
                {contactType === 'person' ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        Name
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                        {formData.fullName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        Gender
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                        {formData.gender || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        Email
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.875rem' }, wordBreak: 'break-all' }}>
                        {formData.email}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        Phone
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                        {formData.phoneNumber}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        Job Title
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                        {formData.jobTitle || 'N/A'}
                      </Typography>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        Company Name
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                        {formData.companyName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        Email
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.875rem' }, wordBreak: 'break-all' }}>
                        {formData.companyEmail}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        Phone
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                        {formData.companyPhone}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        Industry
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                        {formData.industry || 'N/A'}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 2 }} justifyContent="center">
              <Button
                variant="contained"
                onClick={handleNewContact}
                sx={{ 
                  px: { xs: 3, sm: 4 }, 
                  py: { xs: 1.25, sm: 1.5 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  bgcolor: '#3b82f6',
                  '&:hover': { bgcolor: '#2563eb' },
                }}
              >
                Add Another Contact
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard')}
                sx={{ 
                  px: { xs: 3, sm: 4 }, 
                  py: { xs: 1.25, sm: 1.5 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                View All Contacts
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 0 } }}>
      <Container maxWidth="lg">
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4, lg: 5 }, borderRadius: { xs: 2, md: 3 }, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
            <IconButton 
              onClick={() => navigate('/dashboard')} 
              sx={{ 
                bgcolor: '#f1f5f9',
                '&:hover': { bgcolor: '#e2e8f0' },
                width: { xs: 36, sm: 44 },
                height: { xs: 36, sm: 44 },
              }}
            >
              <ArrowBack sx={{ fontSize: { xs: 18, sm: 24 } }} />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' } }}>
                Add New Contact
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Fill in the contact information to add them to your database
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ mb: { xs: 2, sm: 3, md: 4 } }} />

          {/* Contact Type Selector */}
          <Box sx={{ mb: { xs: 3, sm: 4 }, display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={contactType}
              exclusive
              onChange={handleContactTypeChange}
              orientation={isMobile ? 'vertical' : 'horizontal'}
              sx={{
                bgcolor: '#f8fafc',
                width: { xs: '100%', sm: 'auto' },
                '& .MuiToggleButton-root': {
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1.25, sm: 1.5 },
                  border: '2px solid #e2e8f0',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '&.Mui-selected': {
                    bgcolor: '#3b82f6',
                    color: '#fff',
                    border: '2px solid #3b82f6',
                    '&:hover': {
                      bgcolor: '#2563eb',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="person">
                <AccountCircle sx={{ mr: 1, fontSize: { xs: 18, sm: 24 } }} />
                Individual Person
              </ToggleButton>
              <ToggleButton value="company">
                <Business sx={{ mr: 1, fontSize: { xs: 18, sm: 24 } }} />
                Company
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Template Selector */}
          <Paper 
            sx={{ 
              mb: { xs: 3, sm: 4 }, 
              p: { xs: 2, sm: 3 }, 
              bgcolor: '#f8fafc',
              border: '2px dashed #cbd5e1',
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              📋 Quick Fill Templates (Optional)
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Select a template to auto-fill the form with sample data. You can modify any field after applying.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} flexWrap="wrap">
              {Object.keys(contactTemplates[contactType]).map((templateName) => (
                <Button
                  key={templateName}
                  variant={selectedTemplate === templateName ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => applyTemplate(templateName)}
                  sx={{
                    textTransform: 'none',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 0.75, sm: 1 },
                    borderColor: selectedTemplate === templateName ? '#3b82f6' : '#cbd5e1',
                    bgcolor: selectedTemplate === templateName ? '#3b82f6' : 'transparent',
                    color: selectedTemplate === templateName ? '#fff' : '#64748b',
                    '&:hover': {
                      bgcolor: selectedTemplate === templateName ? '#2563eb' : '#f1f5f9',
                      borderColor: selectedTemplate === templateName ? '#2563eb' : '#94a3b8',
                    },
                  }}
                >
                  {templateName}
                </Button>
              ))}
              {selectedTemplate && (
                <Button
                  variant="text"
                  size="small"
                  onClick={clearTemplate}
                  sx={{
                    textTransform: 'none',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    color: '#ef4444',
                    '&:hover': {
                      bgcolor: '#fef2f2',
                    },
                  }}
                >
                  Clear Template
                </Button>
              )}
            </Stack>
          </Paper>

          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {/* Person Fields */}
            {contactType === 'person' && (
              <>
                {/* Basic Information Section */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                    <Person sx={{ mr: 1, color: '#3b82f6', fontSize: { xs: 20, sm: 24 } }} />
                    Personal Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    required
                    value={formData.fullName}
                    onChange={handleChange('fullName')}
                    error={!!errors.fullName}
                    helperText={errors.fullName || 'Enter the contact\'s full name'}
                    variant="outlined"
                    placeholder="e.g., John Smith"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person sx={{ color: '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  {/* Gender radio group */}
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Gender <span style={{ color: '#ef4444' }}>*</span></Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Button
                        variant={formData.gender === 'Male' ? 'contained' : 'outlined'}
                        color={formData.gender === 'Male' ? 'primary' : 'inherit'}
                        onClick={() => setFormData({ ...formData, gender: 'Male' })}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Male
                      </Button>
                      <Button
                        variant={formData.gender === 'Female' ? 'contained' : 'outlined'}
                        color={formData.gender === 'Female' ? 'primary' : 'inherit'}
                        onClick={() => setFormData({ ...formData, gender: 'Female' })}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Female
                      </Button>
                      <Button
                        variant={formData.gender === 'Other' ? 'contained' : 'outlined'}
                        color={formData.gender === 'Other' ? 'primary' : 'inherit'}
                        onClick={() => setFormData({ ...formData, gender: 'Other' })}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Other
                      </Button>
                    </Stack>
                    {errors.gender && (
                      <Typography variant="caption" sx={{ color: '#ef4444', mt: 0.5 }}>{errors.gender}</Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={handleChange('email')}
                    error={!!errors.email}
                    helperText={errors.email || 'Enter a valid email address'}
                    variant="outlined"
                    placeholder="e.g., john.smith@company.com"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={formData.phoneNumber}
                    onChange={handleChange('phoneNumber')}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber || 'Include country code if international'}
                    variant="outlined"
                    placeholder="e.g., +1 (555) 123-4567"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone sx={{ color: '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="WhatsApp Number"
                    value={formData.whatsappNumber}
                    onChange={handleChange('whatsappNumber')}
                    error={!!errors.whatsappNumber}
                    helperText={errors.whatsappNumber || 'WhatsApp contact number (if different from phone)'}
                    variant="outlined"
                    placeholder="e.g., +1 (555) 123-4567"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WhatsApp sx={{ color: '#25D366' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Job Title"
                    value={formData.jobTitle}
                    onChange={handleChange('jobTitle')}
                    variant="outlined"
                    placeholder="e.g., Marketing Manager"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Work sx={{ color: '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="LinkedIn Profile"
                    value={formData.linkedinProfile}
                    onChange={handleChange('linkedinProfile')}
                    variant="outlined"
                    placeholder="e.g., https://linkedin.com/in/johnsmith"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkedIn sx={{ color: '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </>
            )}

            {/* Company Fields */}
            {contactType === 'company' && (
              <>
                {/* Company Information Section */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                    <Business sx={{ mr: 1, color: '#3b82f6', fontSize: { xs: 20, sm: 24 } }} />
                    Company Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    required
                    value={formData.companyName}
                    onChange={handleChange('companyName')}
                    error={!!errors.companyName}
                    helperText={errors.companyName || 'Enter the company name'}
                    variant="outlined"
                    placeholder="e.g., Acme Corporation"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business sx={{ color: '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company Email"
                    type="email"
                    value={formData.companyEmail}
                    onChange={handleChange('companyEmail')}
                    error={!!errors.companyEmail}
                    helperText={errors.companyEmail || 'Main company email address'}
                    variant="outlined"
                    placeholder="e.g., info@acme.com"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company Phone"
                    value={formData.companyPhone}
                    onChange={handleChange('companyPhone')}
                    error={!!errors.companyPhone}
                    helperText={errors.companyPhone || 'Main company phone number'}
                    variant="outlined"
                    placeholder="e.g., +1 (555) 123-4567"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone sx={{ color: '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="WhatsApp Number"
                    value={formData.companyWhatsapp}
                    onChange={handleChange('companyWhatsapp')}
                    error={!!errors.companyWhatsapp}
                    helperText={errors.companyWhatsapp || 'Company WhatsApp contact (if available)'}
                    variant="outlined"
                    placeholder="e.g., +1 (555) 123-4567"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WhatsApp sx={{ color: '#25D366' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Industry"
                    value={formData.industry}
                    onChange={handleChange('industry')}
                    helperText="Select the company's industry"
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Category sx={{ color: '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="">
                      <em>Select an industry</em>
                    </MenuItem>
                    {industryOptions.map((industry) => (
                      <MenuItem key={industry} value={industry}>
                        {industry}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Company Size"
                    value={formData.companySize}
                    onChange={handleChange('companySize')}
                    helperText="Number of employees"
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <People sx={{ color: '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="">
                      <em>Select company size</em>
                    </MenuItem>
                    {companySizeOptions.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

            {/* Extended profile: personal / business details, education, relationship */}
            <Grid item xs={12} sx={{ mt: { xs: 1, sm: 2 } }}>
              <ContactExtraSections
                contactType={contactType}
                formData={formData}
                errors={errors}
                onFieldChange={handleFieldValue}
              />
            </Grid>

            {/* Shared Fields - Website */}
            <Grid item xs={12} sx={{ mt: { xs: 1, sm: 2 } }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                <Language sx={{ mr: 1, color: '#3b82f6', fontSize: { xs: 20, sm: 24 } }} />
                {contactType === 'person' ? 'Additional' : 'Online Presence'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={handleChange('website')}
                variant="outlined"
                placeholder="e.g., https://www.example.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Language sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tags"
                value={formData.tags}
                onChange={handleChange('tags')}
                variant="outlined"
                placeholder="e.g., VIP, Enterprise, Hot Lead (comma separated)"
                helperText="Add tags separated by commas"
              />
            </Grid>

            {/* Location Section */}
            <Grid item xs={12} sx={{ mt: { xs: 1, sm: 2 } }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                <LocationOn sx={{ mr: 1, color: '#3b82f6', fontSize: { xs: 20, sm: 24 } }} />
                Location
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Country"
                required
                value={formData.country}
                onChange={handleChange('country')}
                error={!!errors.country}
                helperText={errors.country || 'Select the country'}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Public sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="">
                  <em>Select a country</em>
                </MenuItem>
                {countryOptions.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="State/Province"
                value={formData.state}
                onChange={handleChange('state')}
                variant="outlined"
                placeholder="e.g., Karnataka, Maharashtra"
                helperText="State or province name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="District"
                value={formData.district}
                onChange={handleChange('district')}
                variant="outlined"
                placeholder="e.g., Bangalore Urban, Mumbai"
                helperText="District or region name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={handleChange('city')}
                variant="outlined"
                placeholder="e.g., Bangalore, Mumbai"
                helperText="City or town name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Postcode/ZIP"
                value={formData.postcode}
                onChange={handleChange('postcode')}
                variant="outlined"
                placeholder="e.g., 560001, 400001"
                helperText="Postal code or ZIP code"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.address}
                onChange={handleChange('address')}
                variant="outlined"
                placeholder="e.g., 123 Main Street, Suite 100"
                helperText="Full street address"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Notes Section */}
            <Grid item xs={12} sx={{ mt: { xs: 1, sm: 2 } }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                <Notes sx={{ mr: 1, color: '#3b82f6', fontSize: { xs: 20, sm: 24 } }} />
                Additional Notes
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={{ xs: 3, sm: 4 }}
                label="Notes"
                value={formData.notes}
                onChange={handleChange('notes')}
                variant="outlined"
                placeholder="Add any additional information about this contact..."
                helperText="Optional: Include any relevant details, conversation history, or special requirements"
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>

            {/* Error Alert */}
            {errors.submit && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mt: { xs: 1, sm: 2 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {errors.submit}
                </Alert>
              </Grid>
            )}

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />
            </Grid>

            <Grid item xs={12}>
              <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={{ xs: 1.5, sm: 2 }} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/dashboard')}
                  sx={{
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1.25, sm: 1.5 },
                    borderColor: '#e2e8f0',
                    color: '#64748b',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    '&:hover': {
                      borderColor: '#cbd5e1',
                      bgcolor: '#f8fafc',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  sx={{
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1.25, sm: 1.5 },
                    bgcolor: '#3b82f6',
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.25)',
                    '&:hover': {
                      bgcolor: '#2563eb',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.35)',
                    },
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={{ xs: 18, sm: 20 }} sx={{ mr: 1, color: 'white' }} />
                      Creating Contact...
                    </>
                  ) : (
                    <>
                      <CheckCircle sx={{ mr: 1, fontSize: { xs: 18, sm: 20 } }} />
                      Create Contact
                    </>
                  )}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default CreateContact;
